import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';

import { useCommentContext } from '@work4all/utils';

export function useConfirmBeforeCloseMask(
  enabled: boolean,
  onBeforeClose?: () => void,
  closeOnNavigation?: boolean
) {
  const { t } = useTranslation();

  const { setObjectionListener, confirm, close } = useHistoryStack();

  const { comment } = useCommentContext();

  const isCommentEmpty = !comment;
  const shouldConfirm = enabled || !isCommentEmpty;

  const warningMessage = !isCommentEmpty
    ? t('ALERTS.UNSAVED_COMMENT')
    : t('ALERTS.UNSAVED_CHANGES');

  useEffect(() => {
    if (shouldConfirm) {
      setObjectionListener(() => {
        return confirm(warningMessage).then((response) => {
          if (response) {
            onBeforeClose?.();
          }
          return response;
        });
      });

      return () => {
        setObjectionListener(null);
      };
    }
  }, [
    confirm,
    setObjectionListener,
    shouldConfirm,
    warningMessage,
    onBeforeClose,
  ]);

  // When closeOnNavigation is true, always block navigation and close dialog instead
  const shouldBlock =
    closeOnNavigation || (shouldConfirm && !closeOnNavigation);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return shouldBlock && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (closeOnNavigation) {
        // For nested dialogs: if dirty, show confirmation before closing; if clean, just close
        if (shouldConfirm) {
          confirm(warningMessage).then((response) => {
            if (response) {
              // User confirmed - close the dialog and prevent navigation
              setObjectionListener(null);
              close();
              blocker.reset();
            } else {
              // User cancelled - keep dialog open and prevent navigation
              blocker.reset();
            }
          });
        } else {
          // Not dirty - just close dialog and prevent navigation
          setObjectionListener(null);
          close();
          blocker.reset();
        }
      } else {
        // Show confirmation dialog for normal navigation
        confirm(warningMessage).then((response) => {
          if (response) {
            blocker.proceed();
          } else {
            blocker.reset();
          }
        });
      }
    }
  }, [
    blocker,
    confirm,
    warningMessage,
    closeOnNavigation,
    close,
    shouldConfirm,
    setObjectionListener,
  ]);
}
